/**
 * Pure helpers for the Social presence section of the Marketing tab.
 *
 * The server keeps the BARE handle for each platform and the FULL url for the
 * website. These helpers strip any pasted leading `@`, protocol, or domain so
 * what gets saved is just the handle, tidy older rows the same way on read, and
 * build the canonical link-out url for read mode. They are kept apart from the
 * components so they are unit-testable.
 */
import type { SocialPlatform } from '../../../../api/venues';

/**
 * Reduce any user/legacy value to the bare handle: drop a protocol, a leading
 * `@`, and a known social domain prefix (with optional `www.`), then trim a
 * trailing slash and surrounding whitespace. Handles paste of a full profile
 * url (`https://instagram.com/foo/`), a `facebook.com/foo` legacy value, or a
 * plain `@foo`.
 */
export function normalizeHandle(raw: string | null | undefined): string {
  if (!raw) return '';
  let h = raw.trim();
  // Strip protocol.
  h = h.replace(/^https?:\/\//i, '');
  // Strip a known social domain (with optional www. and optional leading @
  // before the domain has already been removed). Order matters: tiktok urls
  // include an `@` after the domain, so handle the domain first.
  h = h.replace(/^www\./i, '');
  h = h.replace(/^(?:m\.)?instagram\.com\//i, '');
  h = h.replace(/^(?:web\.|m\.)?facebook\.com\//i, '');
  h = h.replace(/^(?:m\.)?tiktok\.com\//i, '');
  // Strip a leading @ (tiktok urls keep one after the domain; bare values too).
  h = h.replace(/^@+/, '');
  // Drop any trailing path/query and trailing slash.
  h = h.split(/[/?#]/)[0];
  return h.trim();
}

/** True when `url` is a syntactically valid http(s) address. */
export function isValidWebsiteUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const u = new URL(url.trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/** A bare domain such as `example.com` becomes `https://example.com`. Anything
 *  that already names a scheme is left as typed, so the server can judge it. */
export function withHttpsScheme(raw: string): string {
  const typed = raw.trim();
  return /^[a-z][a-z0-9+.-]*:/i.test(typed) ? typed : `https://${typed}`;
}

export interface SocialDisplay {
  /** Visible text (e.g. `@foo` for IG/TikTok, `foo` for FB). */
  text: string;
  /** Canonical link-out url. */
  href: string;
}

const URL_BUILDERS: Record<SocialPlatform, (h: string) => string> = {
  instagram: (h) => `https://instagram.com/${h}`,
  tiktok: (h) => `https://tiktok.com/@${h}`,
  facebook: (h) => `https://facebook.com/${h}`,
};

const TEXT_BUILDERS: Record<SocialPlatform, (h: string) => string> = {
  instagram: (h) => `@${h}`,
  tiktok: (h) => `@${h}`,
  facebook: (h) => h,
};

/**
 * Build the read-mode display (text + canonical href) for a handle-based
 * platform. Normalizes legacy values first. Returns null when there's no handle.
 */
export function socialDisplay(
  platform: SocialPlatform,
  raw: string | null | undefined,
): SocialDisplay | null {
  const handle = normalizeHandle(raw);
  if (!handle) return null;
  return { text: TEXT_BUILDERS[platform](handle), href: URL_BUILDERS[platform](handle) };
}

/**
 * Build the read-mode display for the Website field. Text and href are both the
 * url; returns null when the value is missing or fails url validation (the
 * caller renders plain text in that case).
 */
export function websiteDisplay(raw: string | null | undefined): SocialDisplay | null {
  const url = raw?.trim();
  if (!url || !isValidWebsiteUrl(url)) return null;
  return { text: url, href: url };
}
